import { HttpClient } from '@/data/protocols/http'
import { GenericResponse } from '@/infra/http'
import { BaseRemote } from '../base-remote'
import { QueryClient } from 'react-query'
import { Category } from '@/domain/models/content/category'
import { Subtype } from '@/domain/models/content/subtype'
import { EnrollmentStatus } from '@/domain/models/enrollment/status'

export class RemoteAccountEnrollmentDetail extends BaseRemote {
  constructor(
    protected readonly queryClient: QueryClient,
    protected readonly url: string,
    protected readonly httpClient: HttpClient<GenericResponse<RemoteAccountEnrollmentDetail.Model>>
  ) {
    super()
  }

  async loader(id: string): Promise<RemoteAccountEnrollmentDetail.Model> {
    return this.queryClient.fetchQuery({
      queryKey: `${RemoteAccountEnrollmentDetail.Key}-${id}`,
      queryFn: () => this.getById(id),
      staleTime: 1000
    })
  }

  async getById(id: string): Promise<GenericResponse<RemoteAccountEnrollmentDetail.Model>> {
    const response = await this.httpClient.request({
      url: `${this.url}/${id}`,
      method: 'get'
    })
    return this.onSuccess(response, RemoteAccountEnrollmentDetail.parseResponse)
  }

  private static enrichEnrollmentInfo(
    enrollment: RemoteAccountEnrollmentDetail.Model['authorizationInfo']
  ): RemoteAccountEnrollmentDetail.Model['authorizationInfo'] {
    const category = Category.getById(enrollment.categoryId)
    const subtype =
      enrollment.subTypeId !== null ? Subtype.getById(Number(enrollment.subTypeId)) : null
    const currentStatus = EnrollmentStatus.getById(enrollment.status)
    const isUnitEnabledCategory = [
      Category.Enum.UNDERGRADUATE.id,
      Category.Enum.GRADUATE.id
    ].includes(enrollment.categoryId)

    return {
      ...enrollment,
      category,
      subtype,
      currentStatus,
      isUnitEnabledCategory
    }
  }

  public static parseResponse(
    res: GenericResponse<RemoteAccountEnrollmentDetail.Model>
  ): GenericResponse<RemoteAccountEnrollmentDetail.Model> {
    const data = res.data!

    return {
      ...res,
      data: {
        ...data,
        authorizationInfo: RemoteAccountEnrollmentDetail.enrichEnrollmentInfo(
          data.authorizationInfo
        )
      }
    }
  }
}

export namespace RemoteAccountEnrollmentDetail {
  export type TUnitInfo = {
    frequency: string | null
    startDateInfo: {
      shift: string | null
      startAt: string | null
    }
    unit?: {
      name?: string
      address?: {
        country?: string
        state?: string
        city?: string
        neighborhood?: string
        location?: string
        complement?: string
        zipCode?: string
      }
    }
  }

  export type Model = {
    authorizationInfo: {
      companyName: string
      name: string
      type: string
      courseMonthlyDuration: number | null
      skills: { id: number; name: string }[]
      createdAt: string
      approvedAt?: string
      expiresAt?: string
      id: number
      categoryId: number
      status: number
      institutionUserPlatformUrl: string | null
      institutionName: string
      institutionId: number
      subTypeId: number | null
      subtype: Subtype.Enum | null
      category: Category.Enum
      currentStatus: EnrollmentStatus.Enum
      isUnitEnabledCategory: boolean
    }
    unitInfo: TUnitInfo | null
  }

  export const Key = 'user-enrollment-detail-id'
}
